import React, { ReactNode, useEffect, useRef } from 'react';

interface PurchaseTermsModalProps {
  title: string;
  summary: string;
  price: string;
  children: ReactNode;
  purchaseDisabled?: boolean;
  purchasePending?: boolean;
  returnFocusSelector?: string;
  onPurchase: () => void;
  onCancel: () => void;
}

const PurchaseTermsModal: React.FC<PurchaseTermsModalProps> = ({
  title,
  summary,
  price,
  children,
  purchaseDisabled = false,
  purchasePending = false,
  returnFocusSelector,
  onPurchase,
  onCancel,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const callbacks = useRef({ onCancel, purchasePending });
  callbacks.current = { onCancel, purchasePending };

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cancelRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !callbacks.current.purchasePending) {
        event.preventDefault();
        callbacks.current.onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(cardRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]') || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      window.setTimeout(() => {
        const fallback = returnFocusSelector ? document.querySelector<HTMLElement>(returnFocusSelector) : null;
        const target = previousFocus && previousFocus !== document.body && previousFocus.isConnected
          ? previousFocus : fallback;
        target?.focus();
      }, 0);
    };
  }, [returnFocusSelector]);

  return (
    <div className="u2-modal is-open sms-purchaseModal" role="presentation">
      <div className="u2-modalBackdrop" aria-hidden="true" onMouseDown={() => { if (!purchasePending) onCancel(); }} />
      <div ref={cardRef} className="u2-modalCard sms-purchaseModal__card" role="dialog" aria-modal="true" aria-labelledby="purchase-terms-title">
        <div className="u2-modalHeader sms-purchaseModal__header">
          <div><h2 id="purchase-terms-title">{title}</h2><p>{summary}</p></div>
          <strong>{price}</strong>
        </div>
        <div className="u2-modalBody sms-purchaseModal__body">
          {children}
          <div className="sms-longRent__confirmActions">
            <button ref={cancelRef} type="button" className="u2-btn is-secondary" onClick={onCancel} disabled={purchasePending}>Отказаться</button>
            <button type="button" className="u2-btn is-primary" onClick={onPurchase} disabled={purchaseDisabled || purchasePending}>{purchasePending ? 'Покупаем…' : 'Купить'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseTermsModal;
